import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Search as SearchIcon, CheckCircle as EnableIcon, Cancel as DisableIcon } from '@mui/icons-material';
import { adminApi, FeatureDefinition, Organization } from '../services/api';
import {
  summariseBulkUpdate,
  makeOrganizationNameResolver,
  BulkUpdateOutcome,
} from './feature-bulk-update-outcome';

const FeaturesPage: React.FC = () => {
  const [features, setFeatures] = useState<FeatureDefinition[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('all');
  const [selectedFeature, setSelectedFeature] = useState<FeatureDefinition | null>(null);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'enable' | 'disable'>('enable');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionError, setBulkActionError] = useState<string | null>(null);
  // Populated only when the server refused at least one organisation. Holding it
  // in state is what keeps the dialog open — closing on a refusal is what made a
  // rejected enable look like a successful one.
  const [bulkActionOutcome, setBulkActionOutcome] = useState<BulkUpdateOutcome | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [featuresResponse, orgsResponse] = await Promise.all([
        adminApi.listFeatures(),
        adminApi.listOrganizations()
      ]);
      
      setFeatures(featuresResponse.data.features);
      setOrganizations(orgsResponse.data.organizations);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = (feature: FeatureDefinition, action: 'enable' | 'disable') => {
    setSelectedFeature(feature);
    setBulkAction(action);
    setBulkActionError(null);
    setBulkActionOutcome(null);
    setBulkActionDialogOpen(true);
  };

  const executeBulkAction = async () => {
    if (!selectedFeature) return;

    try {
      setBulkActionLoading(true);
      setBulkActionError(null);
      // Clear the PREVIOUS refusal. "Try again" re-enters HERE, not through
      // handleBulkAction, so without this the old refusal sits under the
      // "Processing..." button — and a retry that throws renders the stale refusal
      // Alert and the new error Alert together, two contradictory verdicts at once.
      setBulkActionOutcome(null);

      const response = await adminApi.bulkUpdateFeature({
        featureId: selectedFeature.id,
        action: bulkAction,
        organizationIds: ['all']
      });

      // Refresh first, and unconditionally: a partly-refused run still wrote to
      // the organisations it accepted, so the grid must show the new state even
      // though the dialog is about to report failures.
      const orgsResponse = await adminApi.listOrganizations();
      const freshOrganizations = orgsResponse.data.organizations;
      setOrganizations(freshOrganizations);

      const outcome = summariseBulkUpdate(
        response,
        makeOrganizationNameResolver(freshOrganizations),
      );
      // Gate on "is there something to show", not on the count — they agree by
      // construction, but the group list is what the Alert renders.
      if (outcome.failureGroups.length > 0) {
        // Stay open. The endpoint answers 200 for a refusal, so closing here is
        // exactly how a rejected enable used to read as a successful one.
        setBulkActionOutcome(outcome);
        return;
      }

      setBulkActionDialogOpen(false);
      setSelectedFeature(null);
    } catch (err: any) {
      setBulkActionError(err.response?.data?.message || err.message || 'Bulk action failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const getFeatureRolloutCount = (featureId: string): number => {
    return organizations.filter(org => 
      (org.enabledFeatures || org.features || []).includes(featureId)
    ).length;
  };

  // Filter features
  const filteredFeatures = features.filter(feature => {
    const matchesSearch = feature.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         feature.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         feature.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || feature.category === categoryFilter;
    const matchesVisibility = visibilityFilter === 'all' || feature.visibility === visibilityFilter;
    
    return matchesSearch && matchesCategory && matchesVisibility;
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Feature Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'visibility',
      headerName: 'Type',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={params.value === 'GENERAL' ? 'success' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'rollout',
      headerName: 'Rollout',
      width: 120,
      valueGetter: (value, row) => {
        const count = getFeatureRolloutCount(row.id);
        return `${count}/${organizations.length}`;
      },
      renderCell: (params: GridRenderCellParams) => {
        const count = getFeatureRolloutCount(params.row.id);
        const percentage = organizations.length > 0 ? (count / organizations.length) * 100 : 0;
        return (
          <Chip 
            label={`${count}/${organizations.length} orgs`}
            size="small"
            color={percentage === 100 ? 'success' : percentage > 0 ? 'primary' : 'default'}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Bulk Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
          <Tooltip title="Enable for all organizations">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleBulkAction(params.row, 'enable')}
            >
              <EnableIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Disable for all organizations">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleBulkAction(params.row, 'disable')}
            >
              <DisableIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            Features Management
          </Typography>
          <Chip label={`${features.length} features`} color="primary" variant="outlined" />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search features..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="ASSESSMENT">Assessment</MenuItem>
              <MenuItem value="POLICY">Policy</MenuItem>
              <MenuItem value="MEDICAL">Medical</MenuItem>
              <MenuItem value="BROKER_PERSONAL">Broker - Personal</MenuItem>
              <MenuItem value="BROKER_COMMERCIAL">Broker - Commercial</MenuItem>
              <MenuItem value="BROKER_LTI">Broker - LTI</MenuItem>
              <MenuItem value="BROKER_TOOLS">Broker - Tools</MenuItem>
              <MenuItem value="RESOURCES">Resources</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={visibilityFilter}
              label="Type"
              onChange={(e) => setVisibilityFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="GENERAL">General</MenuItem>
              <MenuItem value="PROPRIETARY">Proprietary</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ml: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              {filteredFeatures.length} of {features.length} features shown
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Features Table */}
      <Card>
        <DataGrid
          rows={filteredFeatures}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          }}
        />
      </Card>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog
        open={bulkActionDialogOpen}
        onClose={() => !bulkActionLoading && setBulkActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bulkAction === 'enable' ? 'Enable' : 'Disable'} Feature for All Organizations
        </DialogTitle>
        <DialogContent>
          {bulkActionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBulkActionError(null)}>
              {bulkActionError}
            </Alert>
          )}
          
          {bulkActionOutcome && (
            <Alert
              severity={bulkActionOutcome.succeeded === 0 ? 'error' : 'warning'}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {bulkActionOutcome.succeeded === 0
                  ? `No organisations were changed — all ${bulkActionOutcome.failed} were refused.`
                  : `${bulkActionOutcome.succeeded} organisation(s) updated, ${bulkActionOutcome.failed} refused.`}
              </Typography>
              {bulkActionOutcome.failureGroups.map((group, index) => (
                <Box key={index} sx={{ mt: 1 }}>
                  <Typography variant="body2">{group.reason}</Typography>
                  {group.organizationNames.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {group.organizationNames.slice(0, 10).join(', ')}
                      {group.organizationNames.length > 10 &&
                        ` and ${group.organizationNames.length - 10} more`}
                    </Typography>
                  )}
                </Box>
              ))}
            </Alert>
          )}

          {selectedFeature && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to <strong>{bulkAction}</strong> the following feature for all organizations?
              </Typography>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {selectedFeature.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedFeature.id}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedFeature.description}
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                This will affect <strong>{organizations.length} organization(s)</strong> and may impact user access immediately.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)} disabled={bulkActionLoading}>
            {bulkActionOutcome ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color={bulkAction === 'enable' ? 'primary' : 'error'}
            onClick={executeBulkAction}
            disabled={bulkActionLoading}
            startIcon={bulkActionLoading ? <CircularProgress size={20} /> : null}
          >
            {bulkActionLoading
              ? 'Processing...'
              : bulkActionOutcome
                ? 'Try again'
                : `${bulkAction === 'enable' ? 'Enable' : 'Disable'} for All`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeaturesPage;
